import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * <ScrollToTop /> — single, global route-change scroll restoration.
 *
 * Mount once inside <BrowserRouter>, next to <HashScroller />.  Renders
 * nothing.  Contract:
 *
 *   1. On every SPA route change that carries a NEW pathname OR a new
 *      search string, we snap the window to (0, 0).  This is the fix
 *      for the "click category → land on footer" scroll-preservation
 *      bug: without this, `BrowserRouter` keeps whatever scroll offset
 *      the previous page had.
 *
 *   2. Hash anchors (`/#how-it-works`, `/faq#returns`, etc.) are
 *      DELEGATED to <HashScroller />.  When the incoming location has
 *      a non-empty hash we do nothing so the hash-scroller can run its
 *      bounded polling routine and land on the anchor.
 *
 *   3. Browser Back / Forward (`navigationType === "POP"`) is a signal
 *      that the user wants to return to a previously-viewed page at
 *      the position they left it.  We opt OUT of the reset in that
 *      case and let the browser's native scroll restoration (default
 *      `history.scrollRestoration === "auto"`) place the viewport.
 *
 *   4. `window.scrollTo` may not exist in test / SSR environments.
 *      Every call is wrapped in `typeof window !== "undefined"` +
 *      `typeof window.scrollTo === "function"` so this component is
 *      safe to import from jest-dom tests.
 *
 * Deliberately kept as a separate concern from <HashScroller />:
 *   * ScrollToTop = "no hash + new route → top of page".
 *   * HashScroller = "hash present → scroll to that element".
 * The two never fight because the hash branch below short-circuits
 * before we touch the viewport.
 */
export function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // 2) Hash anchors are the HashScroller's job.
    if (hash && hash.length > 1) return;

    // 3) Back / Forward — let the browser restore natural scroll.
    if (navType === "POP") return;

    if (typeof window.scrollTo !== "function") return;

    // Use `auto` (jump), not `smooth` — the destination has just
    // mounted, a smooth animation would look like the page is
    // scrolling *away* from the user's tap.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (_e) {
      // Older Safari: fall back to the 2-arg signature.
      try { window.scrollTo(0, 0); } catch (__e) { /* noop */ }
    }
  }, [pathname, search, hash, navType]);

  return null;
}

export default ScrollToTop;
